import React, { useState, useEffect, useMemo } from 'react';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import {
    CGM_MONTH_OPTIONS,
    getCurrentMonthYear,
    filterByMonthYear,
    buildYearOptions,
} from '../utils/cgmMonthYearFilter';
import { authJsonHeaders } from '../utils/authHeaders';
import { useAuth } from '../contexts/AuthContext';

const { month: DEFAULT_FILTER_MONTH, year: DEFAULT_FILTER_YEAR } = getCurrentMonthYear();

export default function CartesBancairesPage() {
    const { activeCabinet } = useAuth();
    const [charges, setCharges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterMonth, setFilterMonth] = useState(DEFAULT_FILTER_MONTH);
    const [filterYear, setFilterYear] = useState(DEFAULT_FILTER_YEAR);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('');

    useEffect(() => {
        fetchCartesBancaires();
    }, [activeCabinet?.id]);

    const fetchCartesBancaires = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('/api/charges-mensuelles/cartes-bancaires', {
                headers: authJsonHeaders(),
            });
            const data = await response.json();
            if (data.success) {
                setCharges(data.charges || []);
            } else {
                setError(data.error || 'Erreur lors du chargement des opérations carte bancaire');
            }
        } catch (err) {
            setError('Erreur réseau lors du chargement');
            console.error('Erreur:', err);
        } finally {
            setLoading(false);
        }
    };

    const getChargeDate = (charge) => charge.date || charge.date_creation;

    const yearOptions = useMemo(
        () => buildYearOptions(charges, getChargeDate),
        [charges]
    );

    const formatMontant = (montant) => {
        const v = parseFloat(montant || 0);
        return v.toLocaleString('fr-FR', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getClientLabel = (charge) => {
        if (charge.client_nom || charge.client_prenom) {
            return `${charge.client_prenom || ''} ${charge.client_nom || ''}`.trim();
        }
        return charge.client_username || '-';
    };

    const getSourceLabel = (charge) => {
        const src = (charge.source || '').toLowerCase();
        if (src === 'bank_card') return 'Débit carte';
        if (src === 'bureau') return 'Dépense CGM';
        return 'Charge client';
    };

    const getSourceClass = (charge) => {
        const src = (charge.source || '').toLowerCase();
        if (src === 'bureau') return 'cgm';
        return 'client';
    };

    const getSourceKey = (charge) => {
        const src = String(charge.source || '').toLowerCase();
        if (src === 'bank_card') return 'bank_card';
        if (src === 'bureau') return 'bureau';
        return 'client';
    };

    const filteredCharges = useMemo(() => {
        const q = String(searchQuery || '').trim().toLowerCase();
        // Recherche : toutes les opérations ; sinon filtre mois/année
        let list = q
            ? [...charges]
            : filterByMonthYear(charges, filterMonth, filterYear, getChargeDate);

        if (filterType === 'bank_card' || filterType === 'bureau' || filterType === 'client') {
            list = list.filter((c) => getSourceKey(c) === filterType);
        }

        if (!q) return list;

        const qDigits = q.replace(/\s/g, '').replace(',', '.');
        return list.filter((charge) => {
            const client = getClientLabel(charge).toLowerCase();
            const libelle = String(charge.libelle || '').toLowerCase();
            const typeLabel = getSourceLabel(charge).toLowerCase();
            const card = charge.card_id != null ? String(charge.card_id) : '';
            const montantStr = String(charge.montant ?? '').toLowerCase();
            const montantFmt = formatMontant(charge.montant).toLowerCase().replace(/\s/g, '');
            const dateStr = formatDate(getChargeDate(charge)).toLowerCase();
            const hay = `${client} ${libelle} ${typeLabel} ${card} ${montantStr} ${dateStr}`;
            if (hay.includes(q)) return true;
            if (qDigits && (montantStr.includes(qDigits) || montantFmt.includes(qDigits.replace('.', ',')))) {
                return true;
            }
            return false;
        });
    }, [charges, filterMonth, filterYear, filterType, searchQuery]);

    const totalMontant = useMemo(
        () => filteredCharges.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0),
        [filteredCharges]
    );

    const selectedMonthLabel = CGM_MONTH_OPTIONS.find((m) => m.value === filterMonth)?.label;

    if (loading && charges.length === 0) {
        return (
            <CgmModulePageLayout crumb="Comptabilité CGM · Carte bancaire" backTo="/" wide className="cartes-page">
                <div className="text-center py-5">
                    <div className="spinner-border" style={{ color: '#0f766e' }} role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </CgmModulePageLayout>
        );
    }

    return (
        <CgmModulePageLayout crumb="Comptabilité CGM · Carte bancaire" backTo="/" wide className="cartes-page">
            <style jsx global>{`
                .cartes-page {
                    background: transparent;
                    min-height: 0;
                    padding: 0;
                    font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
                    max-width: none;
                    margin: 0;
                }
                .cartes-page .form-container {
                    background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 40%);
                    border-radius: 12px;
                    padding: 0;
                    box-shadow: 0 8px 24px rgba(15, 118, 110, 0.1);
                    border: 1px solid #99f6e4;
                    max-width: 1100px;
                    margin: 0 auto;
                    width: 100%;
                    overflow: hidden;
                }
                .cartes-page .modern-table-container {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 0;
                    overflow: hidden;
                }
                .cartes-page .modern-table-header {
                    background: #0f766e;
                    color: #ffffff;
                    padding: 0.65rem 0.85rem;
                    font-weight: 700;
                    font-size: 0.82rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #115e59;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .cartes-page .modern-table-header .header-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-shrink: 0;
                }
                .cartes-page .modern-table-header .header-filters-scroll {
                    display: flex;
                    align-items: center;
                    min-width: 0;
                    flex: 1;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .cartes-page .modern-table-header .header-filters-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0;
                }
                .cartes-page .modern-table-header .counter {
                    background: rgba(255, 255, 255, 0.2);
                    border: 1px solid rgba(255, 255, 255, 0.35);
                    color: #ffffff;
                    padding: 4px 12px;
                    border-radius: 999px;
                    font-size: 0.78rem;
                    font-weight: 700;
                    white-space: nowrap;
                }
                .cartes-page .caisse-month-year-filters {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                .cartes-page .caisse-month-year-filters label {
                    color: #ffffff;
                    font-weight: 700;
                    font-size: 0.75rem;
                    white-space: nowrap;
                    margin: 0;
                }
                .cartes-page .caisse-month-year-select {
                    border: none;
                    border-radius: 8px;
                    padding: 0.35rem 0.5rem;
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #115e59;
                    background: #ffffff;
                    min-width: 7rem;
                    cursor: pointer;
                }
                .cartes-page .caisse-month-year-select--year {
                    min-width: 5.5rem;
                }
                .cartes-page .modern-table-wrapper {
                    padding: 0;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }
                .cartes-page .modern-table {
                    width: 100%;
                    min-width: 640px;
                    border-collapse: separate;
                    border-spacing: 0;
                    font-size: 0.9rem;
                    border: none;
                }
                .cartes-page .modern-table thead th {
                    background: #0f766e;
                    color: #ffffff;
                    font-weight: 700;
                    padding: 0.6rem 0.75rem;
                    font-size: 0.8rem;
                    text-align: left;
                    border-bottom: 1px solid #115e59;
                    border-right: 1px solid rgba(255, 255, 255, 0.15);
                    white-space: nowrap;
                }
                .cartes-page .modern-table thead th:last-child {
                    border-right: none;
                    text-align: right;
                }
                .cartes-page .modern-table tbody td {
                    padding: 0.65rem 0.75rem;
                    border-bottom: 1px solid #e2e8f0;
                    border-right: 1px solid #e2e8f0;
                    vertical-align: middle;
                    color: #1e293b;
                }
                .cartes-page .modern-table tbody td:last-child {
                    border-right: none;
                    text-align: right;
                }
                .cartes-page .modern-table tbody tr {
                    background: #ffffff;
                    transition: background 0.15s ease;
                }
                .cartes-page .modern-table tbody tr:hover {
                    background: #f0fdfa;
                }
                .cartes-page .modern-table tbody tr:last-child td {
                    border-bottom: none;
                }
                .cartes-page .modern-table tfoot td {
                    background: #f0fdfa;
                    color: #115e59;
                    font-weight: 700;
                    padding: 0.65rem 0.75rem;
                    border-top: 1px solid #99f6e4;
                }
                .cartes-page .modern-table tfoot td:last-child {
                    text-align: right;
                    font-variant-numeric: tabular-nums;
                }
                .cartes-page .modern-type-badge {
                    display: inline-block;
                    padding: 0.25rem 0.65rem;
                    border-radius: 999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    white-space: nowrap;
                }
                .cartes-page .modern-type-badge.client {
                    background: #ccfbf1;
                    color: #115e59;
                    border: 1px solid #99f6e4;
                }
                .cartes-page .modern-type-badge.cgm {
                    background: #fef3c7;
                    color: #92400e;
                    border: 1px solid #fde68a;
                }
                .cartes-page .modern-amount {
                    color: #dc2626;
                    font-weight: 700;
                    font-variant-numeric: tabular-nums;
                }
                .cartes-page .cartes-libelle {
                    max-width: 280px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .cartes-page .modern-empty-state {
                    text-align: center;
                    padding: 2.5rem 1rem;
                    color: #64748b;
                }
                .cartes-page .modern-empty-state i {
                    font-size: 2.5rem;
                    margin-bottom: 0.75rem;
                    opacity: 0.45;
                    display: block;
                    color: #94a3b8;
                }
                .cartes-page .alert {
                    max-width: 1100px;
                    margin: 0 auto 1rem;
                    border-radius: 10px;
                }
                @media (max-width: 768px) {
                    .cartes-page .modern-table-header {
                        flex-direction: row;
                        align-items: center;
                        flex-wrap: wrap;
                    }
                    .cartes-page .modern-table-header .header-title span {
                        font-size: 0.7rem;
                    }
                    .cartes-page .modern-table-header .header-filters-actions {
                        justify-content: flex-end;
                        margin-left: auto;
                    }
                    .cartes-page .modern-empty-state {
                        padding: 1.5rem 0.75rem;
                        font-size: 0.8rem;
                    }
                }
            `}</style>

            <div className="cgm-module-banner">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h2 className="mb-0">
                            <i className="fas fa-credit-card me-2" aria-hidden="true" />
                            Carte bancaire
                        </h2>
                    </div>
                    <div className="text-end">
                        <div className="cgm-module-banner__label">
                            {searchQuery.trim() || filterType
                                ? 'Total (filtre)'
                                : `Total ${selectedMonthLabel} ${filterYear}`}
                        </div>
                        <div className="cgm-module-banner__value">
                            {formatMontant(totalMontant)}
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="fas fa-exclamation-circle me-2" aria-hidden="true" />
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Fermer" />
                </div>
            )}

            <div className="form-container">
                <div className="modern-table-container">
                    <div className="modern-table-header">
                        <div className="header-title">
                            <i className="fas fa-history header-icon" aria-hidden="true" />
                            <span>Historique des opérations</span>
                        </div>

                        <div className="header-filters-scroll">
                            <div className="caisse-month-year-filters">
                                <label htmlFor="cartes-filter-month">Mois</label>
                                <select
                                    id="cartes-filter-month"
                                    className="caisse-month-year-select"
                                    value={filterMonth}
                                    onChange={(e) => setFilterMonth(parseInt(e.target.value, 10))}
                                >
                                    {CGM_MONTH_OPTIONS.map((m) => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </select>
                                <label htmlFor="cartes-filter-year">Année</label>
                                <select
                                    id="cartes-filter-year"
                                    className="caisse-month-year-select caisse-month-year-select--year"
                                    value={filterYear}
                                    onChange={(e) => setFilterYear(parseInt(e.target.value, 10))}
                                >
                                    {yearOptions.map((y) => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="caisse-month-year-filters">
                                <label htmlFor="cartes-filter-type">Type</label>
                                <select
                                    id="cartes-filter-type"
                                    className="caisse-month-year-select"
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                >
                                    <option value="">Tous</option>
                                    <option value="bank_card">Débit carte</option>
                                    <option value="bureau">Dépense CGM</option>
                                    <option value="client">Charge client</option>
                                </select>
                            </div>

                            <div className="caisse-month-year-filters" style={{ flex: '1 1 220px', minWidth: '180px' }}>
                                <label htmlFor="cartes-filter-search">Recherche</label>
                                <input
                                    id="cartes-filter-search"
                                    type="search"
                                    className="caisse-month-year-select"
                                    style={{ minWidth: '180px', width: '100%' }}
                                    placeholder="Client, libellé, montant…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    aria-label="Rechercher par client, libellé ou montant"
                                />
                            </div>
                        </div>

                        <div className="header-filters-actions">
                            <span className="counter">
                                {filteredCharges.length}
                                {charges.length ? ` / ${charges.length}` : ''}
                            </span>
                        </div>
                    </div>

                    <div className="modern-table-wrapper">
                        {charges.length === 0 ? (
                            <div className="modern-empty-state">
                                <i className="fas fa-inbox" aria-hidden="true" />
                                <p>Aucune opération carte bancaire enregistrée</p>
                            </div>
                        ) : filteredCharges.length === 0 ? (
                            <div className="modern-empty-state">
                                <i className="fas fa-calendar-times" aria-hidden="true" />
                                <p>
                                    {searchQuery.trim() || filterType
                                        ? 'Aucun résultat pour cette recherche / ce filtre'
                                        : `Aucune opération pour ${selectedMonthLabel} ${filterYear}`}
                                </p>
                            </div>
                        ) : (
                            <table className="modern-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Client</th>
                                        <th>Libellé</th>
                                        <th>Carte</th>
                                        <th>Type</th>
                                        <th>Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCharges.map((charge) => (
                                        <tr key={charge.id}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{formatDate(getChargeDate(charge))}</td>
                                            <td>{getClientLabel(charge)}</td>
                                            <td className="cartes-libelle" title={charge.libelle || ''}>
                                                {charge.libelle || '-'}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {charge.card_id != null ? `#${charge.card_id}` : '—'}
                                            </td>
                                            <td>
                                                <span className={`modern-type-badge ${getSourceClass(charge)}`}>
                                                    {getSourceLabel(charge)}
                                                </span>
                                            </td>
                                            <td className="modern-amount">{formatMontant(charge.montant || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="5">
                                            TOTAL
                                            {searchQuery.trim() || filterType
                                                ? ' (filtre)'
                                                : ` (${selectedMonthLabel} ${filterYear})`}
                                        </td>
                                        <td>{formatMontant(totalMontant)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </CgmModulePageLayout>
    );
}
